import {
    h, Component, ComponentChildren, ComponentType, VNode, Fragment
} from "preact";
import { useCallback, useLayoutEffect, useRef, useState } from "preact/hooks";
import classname from "../../foundation/utils/classname";
import noderef from "../../foundation/utils/noderef";
import { stopPropagation } from "../../foundation/utils/legacy";
import Portal from "../Portal";
import BasicRecord from "../../foundation/utils/BasicRecord";
import NodeRefs from "../../foundation/utils/NodeRefs";
import { Keyboard } from "../../foundation/enums";
import CSSProperties from "../../foundation/model/CSSProperties";
import { imageUrl } from "../../foundation/utils/image";
import nativeContextMenu from "../../foundation/electron/contextMenu";
import { electronAppSupports } from "../../foundation/electron/electronAppSupports";

import { Props, NativeMenuItemProps } from "./ContextMenuProps";
import { NATIVE_MENU_ERROR_TYPE, addToNativeContextMenuErrorLogger } from "./NativeMenuError";
import "./style.css";

/**
 * This module will help to open a context menu on both web app and electron side. Creating a context menu on web app
 * is easy. ContextMenu component will render all of its children with their css classes to produce the desired
 * outcome. To have a ContextMenu on web app side, we don't have restrictions. A ContextMenu child can be an element,
 * ContextMenu.Item, ContextMenu.ItemDivider or ContextMenu.SubMenu. However, to have a native context menu on our
 * desktop apps, we have some restrictions. To have a native context menu, we first create menu template and pass it
 * to the `nativeContextMenu` module and it will communicate with the electron side. Since native context menus have
 * some restrictions, we had to define some rules on ContextMenu component as well. If we want a child to be in the
 * native menu template, it should be of type ContextMenu.Item, ContextMenu.ItemDivider or ContextMenu.Submenu.
 * Any other component or element won't be added to the native menu. Also the child(ren) of these components must be
 * string as well. Currently(23.01.2023) we have no use of these components having a child other than strings. So if
 * you need it, we need to work on our algorithm a little bit. If you are not sure if native menu will be shown as you
 * expect, you can test it on our windows app or check other usages inside our repository. You could assume there would
 * be changes on the native context menu if you see an error thrown in the console. If you want that error to not to
 * be shown, you can pass 'allowNonNativeMenuItems' prop. The error logs will only be seen in the local environment
 * just to warn developers that native menu may not be created as they wanted. On the other hand, if you want a menu
 * item to be shown differently on native menu, you can pass nativeMenuItems prop to ContextMenu component. Check
 * FlowLink component.
 */

const SCREEN_LIMIT_OFFSET = 12;

interface MenuItemProps {
    children: ComponentChildren;
    onClick?: (ev?: Event) => void;
    warning?: boolean;
    class?: string;
    depth?: number;
    indentation?: number;
    "data-testid"?: string;
}

interface SubMenuProps {
    children: ComponentChildren;
    content: ComponentChildren;
    toLeft?: boolean;
}

interface Position {
    x: number;
    y: number;
}

interface AutoPosition {
    zIndex?: number;
    clickOrigin: Position;
    menuContainer?: string | HTMLElement;
    limitingElement?: string | HTMLElement;
    preventOverflow?: boolean;
    offset?: number;
}

interface AutoPositionedContextMenuProps extends Props {
    autoPosition: AutoPosition;
}

interface AutoPositionedContextMenuState {
    top: string | null;
    left: string | null;
    toLeft: boolean;
    toTop: boolean;
}

interface NodeRef {
    base: HTMLElement;
}

interface OverlayContextMenuProps extends Props {
    autoPosition?: AutoPosition;
    position?: Position;
}

interface OverlayContextMenuState {
    left: number | null;
    top: number | null;
}

class MenuItem extends Component<MenuItemProps> {
    handleClick = (ev: Event) => {
        this.props.onClick?.(ev);
        this.context.close();
    };

    render() {
        const {
            children, warning, class: cssClass, depth, indentation
        } = this.props;
        let computedStyle = {};

        if (depth && indentation) {
            computedStyle = { paddingLeft: 24 + depth * indentation };
        }

        return (
            <button
                type="button"
                class={classname("menuitem noSelect ellipsis", cssClass, { warning, nested: Boolean(depth) })}
                style={computedStyle}
                data-testid={this.props["data-testid"]}
                onClick={this.handleClick}>
                {children}
            </button>
        );
    }
}

function SubMenu({ content, children, toLeft }: SubMenuProps) {
    const [open, setOpen] = useState(false);
    const [subMenuStyle, setSubMenuStyle] = useState<CSSProperties>({});

    const subMenuContentRef = useCallback((node: HTMLDivElement | null) => {
        if (node) {
            const subMenuContentClientRect = node.getBoundingClientRect();
            if (subMenuContentClientRect.bottom > window.innerHeight) {
                setSubMenuStyle({
                    top: Math.max(
                        window.innerHeight - (subMenuContentClientRect.bottom + SCREEN_LIMIT_OFFSET),
                        SCREEN_LIMIT_OFFSET - subMenuContentClientRect.top // if height is more than viewport height, extend up to top of the viewport at max
                    )
                });
            }
        }
    }, []);

    function handleMouseEnter() {
        setOpen(true);
    }

    function handleMouseLeave() {
        setOpen(false);
    }

    return (
        <div class={classname("submenu", { toLeft })} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <MenuItem>{content} <img src={imageUrl("img/icMenuArrow.svg")} alt="&gt;" class="menuChevron"/></MenuItem>
            {open && <div class="submenuContent" ref={subMenuContentRef} style={subMenuStyle}>{children}</div>}
        </div>
    );
}

function MenuItemDivider() {
    return <hr/>;
}

function addToNativeContextMenu(
    children: ComponentChildren,
    nativeMenuItems: NativeMenuItemProps[],
    allowNonNativeMenuItems: boolean
) {
    if (!children || typeof children !== "object") {
        return;
    }

    if (Array.isArray(children)) {
        children.forEach(child => addToNativeContextMenu(child, nativeMenuItems, allowNonNativeMenuItems));
        return;
    }

    if ((children as VNode).type === Fragment) {
        addToNativeContextMenu((children as VNode).props.children, nativeMenuItems, allowNonNativeMenuItems);
        return;
    }

    if ((children as VNode).type === MenuItemDivider) {
        nativeMenuItems.push({ type: "separator" });
    } else if ((children as VNode).type === MenuItem as ComponentType<MenuItemProps>) {
        const props = (children as VNode).props as MenuItemProps;

        if (
            typeof props.children === "string" ||
            (Array.isArray(props.children) && props.children.every(i => typeof i === "string"))
        ) {
            const label = Array.isArray(props.children) ? props.children.join("") : props.children;
            nativeMenuItems.push({
                label: props.depth ? " ".repeat(props.depth * 3) + label : label,
                onClick: props.onClick
            });
        } else {
            addToNativeContextMenuErrorLogger(
                { type: NATIVE_MENU_ERROR_TYPE.MENU_ITEM, child: props.children },
                allowNonNativeMenuItems
            );
        }
    } else if ((children as VNode).type === SubMenu) {
        const props = (children as VNode).props as SubMenuProps;

        if (typeof props.content === "string") {
            const subMenu: NativeMenuItemProps[] = [];
            addToNativeContextMenu(props.children, subMenu, allowNonNativeMenuItems);

            nativeMenuItems.push({
                label: props.content,
                subMenu
            });
        } else {
            addToNativeContextMenuErrorLogger(
                { type: NATIVE_MENU_ERROR_TYPE.SUBMENU_CONTENT, content: props.content },
                allowNonNativeMenuItems
            );
        }
    } else {
        addToNativeContextMenuErrorLogger(
            { type: NATIVE_MENU_ERROR_TYPE.UNSUPPORTED_CHILD, child: children },
            allowNonNativeMenuItems
        );
    }
}

class ContextMenu extends Component
    <(Props | OverlayContextMenuProps | AutoPositionedContextMenuProps) & { type?: "overlay" | "autoPosition"; }> {
    static Item: ComponentType<MenuItemProps>;
    static ItemDivider: () => JSX.Element;
    static SubMenu: ComponentType<SubMenuProps>;
    static withAutoPosition: (props: AutoPositionedContextMenuProps) => JSX.Element;
    static overlay: (props: OverlayContextMenuProps) => JSX.Element;
    nativeMenuShown = false;

    handleOnClose = () => {
        this.nativeMenuShown = false;
        this.props.onClose?.();
    };

    render() {
        const { type, disableNativeMenuItems, ...others } = this.props;

        if (!disableNativeMenuItems && electronAppSupports.nativeContextMenu) {
            if (!this.nativeMenuShown) {
                const { children, nativeMenuItems, allowNonNativeMenuItems = false } = others;

                let items: NativeMenuItemProps[] = [];
                if (nativeMenuItems) {
                    items = nativeMenuItems;
                } else {
                    addToNativeContextMenu(children, items, allowNonNativeMenuItems);
                }

                this.props.onOpen?.();
                this.nativeMenuShown = true;
                nativeContextMenu({ items, onClose: this.handleOnClose });
            }

            return null;
        }

        if (type === "overlay") {
            return <OverlayContextMenu {...others as OverlayContextMenuProps}/>;
        } else if (type === "autoPosition") {
            return <AutoPositionedContextMenu {...others as AutoPositionedContextMenuProps}/>;
        }

        return <DefaultContextMenu {...others as Props}/>;
    }
}

class DefaultContextMenu extends Component<Props> {
    nodeRefs: NodeRefs = {};

    handleOnClose = () => {
        if (this.props.onClose) {
            this.props.onClose();
        }
    };

    handleDocumentClick = (ev: MouseEvent) => {
        // In Firefox; right click on document fires both contextmenu and click events
        // @see: https://bugzilla.mozilla.org/show_bug.cgi?id=184051
        if (ev.button !== Zeplin.MOUSE_BUTTON.LEFT) {
            return;
        }

        if (this.nodeRefs["base"]?.contains(ev.target as HTMLElement)) {
            return;
        }

        this.handleOnClose();
    };

    handleDocumentKeyDown = (ev: KeyboardEvent) => {
        if (ev.key === Keyboard.Key.Escape) {
            ev.stopPropagation();
            this.handleOnClose();
        }
    };

    override componentDidMount() {
        document.addEventListener("contextmenu", this.handleOnClose, true);
        document.addEventListener("click", this.handleDocumentClick, true);
        document.addEventListener("keydown", this.handleDocumentKeyDown, true);

        const { onOpen } = this.props;
        if (onOpen) {
            onOpen();
        }
    }

    override componentWillUnmount() {
        document.removeEventListener("contextmenu", this.handleOnClose, true);
        document.removeEventListener("click", this.handleDocumentClick, true);
        document.removeEventListener("keydown", this.handleDocumentKeyDown, true);
    }

    override getChildContext() {
        return {
            close: this.handleOnClose
        };
    }

    render() {
        const {
            children,
            toLeft,
            toTop,
            class: cssClass,
            style,
            onMouseDown
        } = this.props;
        return (
            <div
                ref={noderef(this, "base")}
                class={classname("contextMenu", cssClass, { toLeft, toTop })}
                style={style}
                data-testid="contextMenu"
                onMouseDown={onMouseDown}
                onClick={stopPropagation}>
                {children}
            </div>
        );
    }
}

function OverlayContextMenu(props: OverlayContextMenuProps) {
    const [state, setState] = useState<OverlayContextMenuState>({
        top: null,
        left: null
    });
    const contextMenuRef = useRef<NodeRef>();

    function setAutoPosition() {
        const { autoPosition } = props;

        if (!autoPosition) {
            return;
        }
        const { clickOrigin, menuContainer } = autoPosition;
        const { left: l, top: t } = state;
        const menuContainerElement = typeof menuContainer === "string"
            ? document.querySelector(menuContainer)
            : menuContainer;
        const menuContainerRect = menuContainerElement?.getBoundingClientRect();
        const rightPositionLimit = menuContainerRect ? menuContainerRect.right : window.innerWidth;
        const bottomPositionLimit = menuContainerRect ? menuContainerRect.bottom : window.innerHeight;
        const menu = contextMenuRef.current!.base;

        const left = Math.min(
            clickOrigin.x + 2,
            rightPositionLimit - menu.offsetWidth - SCREEN_LIMIT_OFFSET
        );
        const top = Math.min(
            clickOrigin.y + 2,
            bottomPositionLimit - menu.offsetHeight - SCREEN_LIMIT_OFFSET
        );

        if (left === l && top === t) {
            return;
        }
        setState({ left, top });
    }

    function getPosition() {
        const { position, autoPosition } = props;

        if (autoPosition) {
            return state;
        }

        if (position) {
            const { x, y } = position;
            return { left: x, top: y };
        }

        return { left: null, top: null };
    }

    useLayoutEffect(() => {
        setAutoPosition();
    });

    const { class: cssClass, autoPosition, ...wrappedProps } = props;
    const { left, top } = getPosition();
    const zIndex = autoPosition?.zIndex ?? null;

    return (
        <Portal>
            <div class="overlayContextMenuContainer">
                <DefaultContextMenu
                    {...wrappedProps}
                    class={classname(cssClass, { autoPositioned: !!autoPosition })}
                    style={{ left, top, zIndex } as CSSProperties}
                    ref={contextMenuRef}/>
            </div>
        </Portal>
    );
}

class AutoPositionedContextMenu extends Component<AutoPositionedContextMenuProps, AutoPositionedContextMenuState> {
    nodeRefs: BasicRecord<{ base: HTMLElement; } | null> = {};

    override state = {
        top: null,
        left: null,
        toLeft: false,
        toTop: false
    };

    autoPosition() {
        const { autoPosition } = this.props;

        if (!autoPosition) {
            return;
        }

        const CONTEXT_MENU_BOX_SHADOW_WIDTH = 4;
        const {
            limitingElement,
            menuContainer,
            clickOrigin,
            preventOverflow,
            offset
        } = autoPosition;
        const { left: l, top: t, toLeft: tl, toTop: tt } = this.state;
        const menu = this.nodeRefs["contextMenu"]!.base;
        const menuContainerElement = typeof menuContainer === "string"
            ? document.querySelector(menuContainer)
            : menuContainer;

        const rect = menuContainerElement!.getBoundingClientRect();
        const limitingPageElement = typeof limitingElement === "string"
            ? document.querySelector(limitingElement)
            : limitingElement;
        const limitingElementWidth = limitingPageElement
            ? window.innerWidth - limitingPageElement.getBoundingClientRect().x + CONTEXT_MENU_BOX_SHADOW_WIDTH
            : 0;

        const left = `${clickOrigin.x - rect.left + 2}px`;
        let top = `${clickOrigin.y - rect.top + 2}px`;
        const toLeft = clickOrigin.x + limitingElementWidth + menu.offsetWidth > window.innerWidth;
        const toTop = clickOrigin.y + menu.offsetHeight > window.innerHeight;
        const containerDistanceToTopOfViewport = rect.top + window.pageYOffset;

        if (toTop && preventOverflow && containerDistanceToTopOfViewport < menu.offsetHeight) {
            top = `${menu.offsetHeight - containerDistanceToTopOfViewport + offset!}px`;
        }
        if (left === l && top === t && toLeft === tl && toTop === tt) {
            return;
        }
        this.setState({
            left,
            top,
            toLeft,
            toTop
        });
    }

    resetAutoPosition() {
        const { autoPosition } = this.props;
        if (!autoPosition) {
            return;
        }
        this.setState({
            left: null,
            top: null,
            toLeft: false,
            toTop: false
        });
    }

    override componentDidMount() {
        this.autoPosition();
    }

    override componentWillReceiveProps() {
        this.resetAutoPosition();
    }

    override componentDidUpdate() {
        this.autoPosition();
    }

    render() {
        const { class: cssClass, autoPosition, ...rest } = this.props;
        const {
            left,
            top,
            toLeft,
            toTop
        } = this.state;
        const wrappedProps = { class: cssClass, ...rest };
        const zIndex = autoPosition?.zIndex ?? null;
        const propOverwrites = {
            toTop,
            toLeft,
            class: classname(cssClass, { autoPositioned: !!autoPosition }),
            style: { left, top, zIndex } as unknown as CSSProperties,
            ref: noderef(this, "contextMenu")
        };
        const contextMenuProps = { ...wrappedProps, ...propOverwrites };

        return <DefaultContextMenu {...contextMenuProps}/>;
    }
}

ContextMenu.Item = MenuItem;
ContextMenu.ItemDivider = MenuItemDivider;
ContextMenu.SubMenu = SubMenu;

ContextMenu.overlay = props => <ContextMenu {...props} type="overlay"/>;
ContextMenu.withAutoPosition = props => <ContextMenu {...props} type="autoPosition"/>;

export default ContextMenu;
